/**
 * Card "lead arrivato da un annuncio Meta" nella cronologia della scheda lead.
 *
 * Stessa forma delle card sessione WhatsApp e Instagram: chiusa mostra miniatura,
 * titolo e due righe di copy; aperta ingrandisce la creatività, apre il testo per
 * intero (area a scorrimento, non spinge via la cronologia) e mostra campagna,
 * gruppo e inserzione. La creatività si apre nel visualizzatore condiviso con
 * zoom e rotazione.
 *
 * Il dato arriva da `useAdCreative`, lo stesso hook della riga Origine del
 * pannello: la prima apertura lo chiede a Meta, dalla seconda risponde la memoria.
 */
import React, { useState } from 'react';
import { Image as ImageIcon, Maximize2, RefreshCw } from 'lucide-react';
import { ImageLightbox } from '../../team-chat/ImageLightbox';
import { CreativeChips } from '../../marketing/AdCreativePreview';
import { useAdCreative } from '../../../lib/hooks/useAdCreative';
import { TimelineFooter } from './TimelineFooter';
import { mediaTileCls } from './cardStyles';

interface ClientAdCreativeCardProps {
  leadId: string;
  adId: string;
  organizationId: string;
  adName?: string | null;
  campaignName?: string | null;
  adsetName?: string | null;
  /** Data di arrivo del lead: attribuzione se c'è, altrimenti creazione. */
  date: string;
  formatDate: (dateString: string) => string;
}

/** Riga "etichetta: valore" del blocco aperto. */
function Riga({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-2 text-[11px] leading-relaxed">
      <span className="w-[86px] flex-shrink-0 text-[#9B9A97] dark:text-white/40">{label}</span>
      <span className="min-w-0 break-words text-[#37352F] dark:text-gray-300">{value}</span>
    </div>
  );
}

export const ClientAdCreativeCard: React.FC<ClientAdCreativeCardProps> = ({
  leadId,
  adId,
  organizationId,
  adName,
  campaignName,
  adsetName,
  date,
  formatDate,
}) => {
  const { creative, loading, enriching, reason } = useAdCreative(leadId, adId, organizationId);
  const [aperto, setAperto] = useState(false);
  const [ingrandita, setIngrandita] = useState(false);
  const [immagineRotta, setImmagineRotta] = useState(false);

  const testo = creative?.body || '';
  const mostraImmagine = !!creative?.imageUrl && !immagineRotta;
  const titolo = creative?.headline || adName || 'Annuncio Meta';

  return (
    <div className="group relative mb-3 rounded-lg border border-[rgba(55,53,47,0.16)] bg-white transition-colors hover:border-[rgba(55,53,47,0.26)] dark:border-[#3F4447] dark:bg-[#2F3437] dark:hover:border-[#565D61]">
      <div className="min-w-0 p-3">
        {loading ? (
          <div className="flex gap-3">
            <div className="h-8 w-8 flex-shrink-0 animate-pulse rounded-lg bg-[rgba(55,53,47,0.08)] dark:bg-white/[0.06]" />
            <div className="min-w-0 flex-1 space-y-1.5 pt-1">
              <div className="h-3 w-2/5 animate-pulse rounded bg-[rgba(55,53,47,0.08)] dark:bg-white/[0.06]" />
              <div className="h-2.5 w-4/5 animate-pulse rounded bg-[rgba(55,53,47,0.06)] dark:bg-white/[0.04]" />
            </div>
          </div>
        ) : aperto ? (
          <div className="flex gap-3">
            {mostraImmagine ? (
              <button
                type="button"
                onClick={() => setIngrandita(true)}
                className="group/img relative h-8 w-8 flex-shrink-0 overflow-hidden rounded-lg border border-[rgba(55,53,47,0.09)] dark:border-white/[0.08]"
                title="Ingrandisci la creatività"
              >
                <img src={creative!.imageUrl!} alt="" className="h-full w-full object-cover" loading="lazy" onError={() => setImmagineRotta(true)} />
              </button>
            ) : (
              <span className={mediaTileCls('marketing')}>
                <ImageIcon size={15} strokeWidth={1.5} />
              </span>
            )}
            <div className="min-w-0 flex-1 space-y-2.5">
              <button onClick={() => setAperto(false)} aria-expanded={true} className="block w-full text-left">
                <div className="text-[13px] font-medium leading-snug text-[#37352F] dark:text-white">{titolo}</div>
              </button>

              {mostraImmagine && (
                <button
                  type="button"
                  onClick={() => setIngrandita(true)}
                  className="group/img relative flex min-h-[132px] w-full items-center justify-center overflow-hidden rounded-md border border-[rgba(55,53,47,0.09)] bg-[#F7F7F5] p-2 dark:border-white/[0.08] dark:bg-white/[0.03]"
                  title="Ingrandisci la creatività"
                >
                  <img
                    src={creative!.imageUrl!}
                    alt={adName || 'Creatività'}
                    className="max-h-72 w-auto object-contain"
                    loading="lazy"
                    onError={() => setImmagineRotta(true)}
                  />
                  <span className="absolute right-2 top-2 hidden rounded-md bg-black/50 p-1 group-hover/img:block">
                    <Maximize2 className="h-3.5 w-3.5 text-white" />
                  </span>
                </button>
              )}

              {testo ? (
                <div className="max-h-64 overflow-y-auto whitespace-pre-line pr-1 text-[12px] leading-relaxed text-[rgba(55,53,47,0.75)] dark:text-gray-400">
                  {testo}
                </div>
              ) : enriching ? (
                <div className="flex items-center gap-1.5 text-[12px] text-[#787774] dark:text-gray-400">
                  <RefreshCw className="h-3 w-3 animate-spin" />
                  Recupero il testo da Meta…
                </div>
              ) : (
                <div className="text-[12px] text-[#787774] dark:text-gray-400">
                  {reason === 'meta_non_collegato' ? 'Testo non disponibile: account Meta da ricollegare' : 'Testo non disponibile'}
                </div>
              )}

              {creative && <CreativeChips creative={creative} />}

              <div className="space-y-1 border-t border-[rgba(55,53,47,0.09)] pt-2 dark:border-[#3F4447]">
                {campaignName && <Riga label="Campagna" value={campaignName} />}
                {adsetName && <Riga label="Gruppo ins." value={adsetName} />}
                {adName && <Riga label="Inserzione" value={adName} />}
              </div>
            </div>
          </div>
        ) : (
          <div className="flex gap-3">
            {mostraImmagine ? (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setIngrandita(true); }}
                className="group/img relative h-8 w-8 flex-shrink-0 overflow-hidden rounded-lg border border-[rgba(55,53,47,0.09)] dark:border-white/[0.08]"
                title="Ingrandisci la creatività"
              >
                <img
                  src={creative!.imageUrl!}
                  alt={adName || 'Creatività'}
                  className="h-full w-full object-cover"
                  loading="lazy"
                  onError={() => setImmagineRotta(true)}
                />
                <span className="absolute inset-0 hidden items-center justify-center bg-black/35 group-hover/img:flex">
                  <Maximize2 className="h-3.5 w-3.5 text-white" />
                </span>
              </button>
            ) : (
              <span className={mediaTileCls('marketing')}>
                <ImageIcon size={15} strokeWidth={1.5} />
              </span>
            )}
            <button onClick={() => setAperto(true)} aria-expanded={false} className="min-w-0 flex-1 text-left">
              <div className="truncate text-[13px] font-medium leading-snug text-[#37352F] dark:text-white">{titolo}</div>
              {testo ? (
                <div className="mt-0.5 line-clamp-2 whitespace-pre-line text-[12px] leading-relaxed text-[rgba(55,53,47,0.7)] dark:text-gray-400">
                  {testo}
                </div>
              ) : enriching ? (
                <div className="mt-1 flex items-center gap-1.5 text-[12px] text-[#787774] dark:text-gray-400">
                  <RefreshCw className="h-3 w-3 animate-spin" />
                  Recupero il testo da Meta…
                </div>
              ) : null}
            </button>
          </div>
        )}
        <TimelineFooter
          tipo="Lead arrivato da Meta Ads"
          accent="marketing"
          date={date}
          titoloData={formatDate(date)}
          expanded={aperto}
        />
      </div>

      {ingrandita && creative?.imageUrl && (
        <ImageLightbox
          images={[{ url: creative.imageUrl, name: adName || creative.headline || 'Creatività' }]}
          initialIndex={0}
          onClose={() => setIngrandita(false)}
        />
      )}
    </div>
  );
};
